import { ErrorBoundary, VStack } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import React, { useMemo } from 'react';

import { CustomerDetailFullAccount } from '../../../../../../store/customerDetail/types';
import { DataListContainer } from '../DataListContainer';
import { InfoContainer } from '../InfoContainer';

import {
  getAdvisorInfos,
  getAgentInfos,
  getInstitutionalInfos,
  getPersonalInfos,
  getResidentialInfos,
} from './constants';

export declare namespace ContainerPersonalDataTypes {
  interface Props {
    customerDetailFull: CustomerDetailFullAccount | null | undefined;
    hasError?: boolean;
    onRefresh?: () => void;
    isLoading?: boolean;
  }
}

export const ContainerPersonalData: React.FC<ContainerPersonalDataTypes.Props> = ({
  customerDetailFull,
  hasError,
  isLoading,
  onRefresh,
}) => {
  const personalInfosColumns = useBreakpointValue({ base: 1, sm: 3, md: 5 });
  const institutionalInfosColumns = useBreakpointValue({ base: 1, sm: 2, md: 4 });
  const agentInfosColumns = useBreakpointValue({ base: 1, sm: 2, md: 4 });
  const residentialInfosColumns = useBreakpointValue({ base: 1, sm: 3, md: 6 });
  const advisorInfosColumns = useBreakpointValue({ base: 1, sm: 2 });

  const personalInfos = useMemo(
    () => getPersonalInfos(customerDetailFull, isLoading || false),
    [customerDetailFull, isLoading]
  );

  const institutionalInfos = useMemo(
    () => getInstitutionalInfos(customerDetailFull, isLoading || false),
    [customerDetailFull, isLoading]
  );

  const agentInfos = useMemo(
    () => getAgentInfos(customerDetailFull, isLoading || false),
    [customerDetailFull, isLoading]
  );

  const residentialInfos = useMemo(
    () => getResidentialInfos(customerDetailFull, isLoading || false),
    [customerDetailFull, isLoading]
  );

  const advisorInfos = useMemo(
    () => getAdvisorInfos(customerDetailFull, isLoading || false),
    [customerDetailFull, isLoading]
  );

  if (hasError) {
    return (
      <DataListContainer>
        <ErrorBoundary onRefresh={onRefresh} />
      </DataListContainer>
    );
  }

  return (
    <DataListContainer>
      <VStack space="stack.xxxs">
        <InfoContainer
          title="Informações pessoais"
          values={personalInfos}
          columns={personalInfosColumns}
        />
        {!!customerDetailFull?.institutionalData?.isCompany && (
          <InfoContainer
            title="Dados Institucionais"
            values={institutionalInfos}
            columns={institutionalInfosColumns}
            forceEqualColumns
          />
        )}
        <InfoContainer
          title="Representante ou Procurador"
          values={agentInfos}
          forceEqualColumns
          columns={agentInfosColumns}
        />
        <InfoContainer
          title="Dados residenciais"
          values={residentialInfos}
          columns={residentialInfosColumns}
        />
        <InfoContainer
          title="Assessor responsável"
          values={advisorInfos}
          columns={advisorInfosColumns}
        />
      </VStack>
    </DataListContainer>
  );
};
